import { ImageBitmapLoader, Texture } from "three";
import type { GLTF, GLTFParser } from "three/addons/loaders/GLTFLoader.js";
import type { Material, Object3D } from "three";
import {
  classifyMobileDevice,
  isMobileDevice,
} from "@runtime/browser/mobileDevice";

/**
 * `textureBudget.ts` — a per-device ceiling on texture DIMENSIONS, enforced at
 * decode time, plus release of the parse-time source bytes the loader used to
 * retain.
 *
 * ## Why this exists (2026-08-27, user-reported iOS crash)
 *
 * Loading the "Bike test" project on a real iPhone crashed the page with
 * Safari's "a problem repeatedly occurred" — the WebContent process killed by
 * the OS for memory, reloaded, and killed again. Measured in the iOS simulator
 * (identical WebKit, no memory ceiling): the tab's resident size went 650 MB →
 * ~2.5 GB while loading ONE 40.5 MB GLB, because the file carries four
 * 8192×8192 JPEGs. Decoded RGBA at 8192² is 268 MB; with the mipmap chain the
 * GPU copy is ~358 MB per texture — 1.43 GB of texture memory from 39.5 MB of
 * JPEG, on a 57k-triangle mesh. Desktop and the simulator absorb that; an
 * iPhone's per-tab limit is far below it, so the page dies AT LOAD, every
 * time. Three clamps only at the hardware maximum (8–16k), so nothing anywhere
 * downsized anything.
 *
 * ## The budget
 *
 * A single per-device ceiling on the longest texture dimension:
 * mobile-class (iOS incl. iPadOS-masquerading-as-Mac, Android incl. Quest) =
 * {@link K_TEXTURE_BUDGET_MOBILE_PX}; desktop = {@link K_TEXTURE_BUDGET_DESKTOP_PX}.
 * Capping 8192² → 1024² cuts that texture's GPU cost 64× (358 MB → 5.6 MB).
 * The desktop value is a DELIBERATE change to desktop rendering too — the same
 * project held desktop Safari's WebContent at a measured 3.5 GB footprint, and
 * 4096 is visually indistinguishable at editor viewing distances. Dial with
 * `Campfire.Dev.TextureBudget` (a number; `0` disables capping) before arguing
 * with it.
 *
 * ## Where it is enforced — THREE seams, each covering what the others cannot
 *
 * 1. **Decode time** ({@link installTextureBudgetOnParser}, registered as a
 *    `GLTFLoader` plugin by `gltfLoaderFactory`): swaps the parser's
 *    `ImageBitmapLoader` for {@link BudgetImageBitmapLoader}, which decodes
 *    SERIALLY (one image at a time) and downscales before handing the result
 *    to three. This is the seam that bounds the PEAK: GLTFLoader on every
 *    modern browser (Safari ≥ 17 included — the UA check in its constructor)
 *    eagerly decodes EVERY embedded image during parse, so four parallel
 *    8192² decodes would transiently hold >1 GB even if capped afterwards.
 * 2. **Post-parse belt** ({@link enforceTextureBudgetOnGltf}, called by
 *    `GltfAssetCache`): walks the parsed scene and caps anything that got
 *    through — the `TextureLoader` fallback path (Safari < 17 / Firefox < 98)
 *    and any extension-loaded plain texture. Compressed / data / video
 *    textures are skipped: their payloads are not canvas-drawable, and a
 *    KTX2/Basis texture is already the SOLUTION to this problem, not part of
 *    it.
 * 3. **Customer material textures** (`campfireMaterialBuilder.loadImage`) and
 *    the custom skybox (`sceneEnvironmentRenderer`) call
 *    {@link capDecodedImageAsync} on their decoded images, so an 8K
 *    `textures/{id}` S3 object or skydome photo obeys the same ceiling.
 *
 * All downscales share ONE module-scoped queue ({@link enqueueDecode}) — a
 * full-size decode is unavoidable while downscaling, so serializing is what
 * keeps the transient bounded to ~one image regardless of how many a scene
 * loads at once.
 *
 * ## What this does not do
 *
 * A dimension cap is not a duplicate collapse. 77 copies of a 2048² JPEG still
 * multiply decode/upload cost even after the mobile cap, so iPhone can crash
 * on count instead of size.
 * Identical embedded `images[]` rows are remapped in `gltfImageDedup.ts` at
 * parse time — the sibling plugin `createGltfLoader` also registers. Do not
 * fold that identity work into this file: budget is size, dedup is sameness.
 */

/** Longest texture side on a mobile-class device (iOS, Android, Quest). */
export const K_TEXTURE_BUDGET_MOBILE_PX = 1024;
/** Longest texture side on a desktop-class device. */
export const K_TEXTURE_BUDGET_DESKTOP_PX = 4096;

/**
 * Dev override: a number; `0` disables capping entirely (the pre-2026-08-27
 * behavior). Read per use like `Campfire.Dev.SkyboxLighting`, so an on-device
 * A/B is "set + reload" with no build.
 */
const K_BUDGET_OVERRIDE_KEY = "Campfire.Dev.TextureBudget";

export type TextureBudgetDeviceClass = "mobile" | "desktop";

/**
 * Pure classification, exported for the harness. `maxTouchPoints` is what
 * unmasks an iPad: iPadOS ≥ 13 Safari reports a macOS UA, and the touch-point
 * count is the documented tell (the XR availability probe uses the same
 * trick). The budget classes by OS, not viewport — a phone in landscape runs
 * the desktop SHELL but keeps a phone's memory.
 */
export function classifyTextureBudgetDevice(
  userAgent: string,
  maxTouchPoints: number,
): TextureBudgetDeviceClass {
  return classifyMobileDevice({
    userAgent,
    platform: "",
    maxTouchPoints,
  }).isMobile
    ? "mobile"
    : "desktop";
}

/** Pure resolution of the effective budget, exported for the harness. */
export function resolveTextureBudget(
  deviceClass: TextureBudgetDeviceClass,
  overrideRaw: string | null,
): number {
  if (overrideRaw !== null) {
    const parsed = Number(overrideRaw);
    if (Number.isFinite(parsed) && parsed >= 0) return Math.floor(parsed);
  }
  return deviceClass === "mobile" ? K_TEXTURE_BUDGET_MOBILE_PX : K_TEXTURE_BUDGET_DESKTOP_PX;
}

/** The effective ceiling for this device, `0` meaning "uncapped". */
export function textureBudgetMaxDimension(): number {
  let override: string | null = null;
  try {
    override = window.localStorage.getItem(K_BUDGET_OVERRIDE_KEY);
  } catch {
    // Private mode / storage disabled — defaults apply.
  }
  let deviceClass: TextureBudgetDeviceClass = "desktop";
  try {
    deviceClass = isMobileDevice() ? "mobile" : "desktop";
  } catch {
    // Headless — desktop default.
  }
  return resolveTextureBudget(deviceClass, override);
}

/**
 * Target dimensions for an image under `maxDimension`, or `null` when it
 * already fits (or the cap is off). Aspect is preserved; the short side floors
 * at 1.
 */
export function fitWithinTextureBudget(
  width: number,
  height: number,
  maxDimension: number,
): { width: number; height: number } | null {
  if (maxDimension <= 0) return null;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  const longest = Math.max(width, height);
  if (longest <= maxDimension) return null;
  const scale = maxDimension / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Image kinds a 2D canvas can draw — i.e. what this module can downscale. */
export type CappableImage = ImageBitmap | HTMLImageElement | HTMLCanvasElement;

function isCappableImage(image: unknown): image is CappableImage {
  if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap) return true;
  if (typeof HTMLImageElement !== "undefined" && image instanceof HTMLImageElement) return true;
  if (typeof HTMLCanvasElement !== "undefined" && image instanceof HTMLCanvasElement) return true;
  return false;
}

function imageDimensions(image: CappableImage): { width: number; height: number } {
  if (typeof HTMLImageElement !== "undefined" && image instanceof HTMLImageElement) {
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  }
  return { width: image.width, height: image.height };
}

/**
 * Decodes (and, while downscaling, briefly double-buffers) one image at a
 * time. The chain settles every job to "done" before the next starts, so one
 * bad image cannot wedge every later decode.
 *
 * NOTE {@link BudgetImageBitmapLoader} runs its decode AND downscale as one
 * queued job — the downscale core is the un-queued {@link downscaleNow}
 * precisely so a queued job never enqueues a second job and deadlocks on
 * itself. Never call {@link capDecodedImageAsync} from inside a queued job.
 */
let s_decodeChain: Promise<unknown> = Promise.resolve();
function enqueueDecode<T>(work: () => Promise<T> | T): Promise<T> {
  const run = s_decodeChain.then(work, work);
  s_decodeChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * Synchronous downscale core. An `ImageBitmap` original is `close()`d after
 * the copy — freeing its decoded pixels NOW rather than at the next GC, which
 * for an 8192² bitmap is a quarter-gigabyte per image of difference while a
 * scene loads. On any failure the ORIGINAL is returned unharmed: rendering a
 * full-size texture is strictly better than rendering nothing.
 */
function downscaleNow(
  image: CappableImage,
  target: { width: number; height: number },
): CappableImage {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext("2d");
    if (!context) return image;
    context.drawImage(image, 0, 0, target.width, target.height);
    if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap) {
      image.close();
    }
    return canvas;
  } catch (err) {
    console.warn("[textureBudget] downscale failed — keeping full-size image:", err);
    return image;
  }
}

/**
 * Downscale `image` under the budget through the shared queue. Returns the
 * input untouched when it already fits or the cap is off.
 */
export function capDecodedImageAsync(
  image: CappableImage,
  maxDimension: number,
): Promise<CappableImage> {
  const { width, height } = imageDimensions(image);
  const target = fitWithinTextureBudget(width, height, maxDimension);
  if (!target) return Promise.resolve(image);
  return enqueueDecode(() => downscaleNow(image, target));
}

/**
 * The serialized, capping replacement for the parser's `ImageBitmapLoader`.
 * Mirrors the upstream `load` pipeline (resolveURL → fetch → blob →
 * `createImageBitmap` with `colorSpaceConversion: 'none'`) minus the
 * three-`Cache` integration, which is disabled by default and redundant under
 * the parser's own per-source cache.
 *
 * `onLoad` may receive a CANVAS rather than an `ImageBitmap` when the image
 * was capped — `GLTFParser.loadImageSource` only ever wraps the result in
 * `new Texture(...)`, for which either is a valid source.
 */
export class BudgetImageBitmapLoader extends ImageBitmapLoader {
  load(
    url: string,
    onLoad?: (data: ImageBitmap) => void,
    _onProgress?: (event: ProgressEvent) => void,
    onError?: (err: unknown) => void,
  ): void {
    const resolved = this.manager.resolveURL(this.path ? this.path + url : url);
    this.manager.itemStart(resolved);
    void (async () => {
      try {
        const response = await fetch(resolved, {
          credentials: this.crossOrigin === "anonymous" ? "same-origin" : "include",
          headers: this.requestHeader,
        });
        const blob = await response.blob();
        const image = await enqueueDecode(async () => {
          const bitmap = await createImageBitmap(blob, {
            ...this.options,
            colorSpaceConversion: "none",
          });
          const target = fitWithinTextureBudget(
            bitmap.width,
            bitmap.height,
            textureBudgetMaxDimension(),
          );
          return target ? downscaleNow(bitmap, target) : bitmap;
        });
        // A capped result is a canvas; the parser only feeds it to
        // `new Texture(...)`, so the widened type never escapes wrongly.
        onLoad?.(image as ImageBitmap);
        this.manager.itemEnd(resolved);
      } catch (err) {
        onError?.(err);
        this.manager.itemError(resolved);
        this.manager.itemEnd(resolved);
      }
    })();
  }
}

/**
 * `GLTFLoader.register` plugin body: swap the parser's texture loader for the
 * capping one. The `TextureLoader` fallback (Safari < 17 / Firefox < 98) is
 * left alone — its decode is lazy, so there is no parse-time peak to bound,
 * and {@link enforceTextureBudgetOnGltf} caps its images after the fact.
 */
export function installTextureBudgetOnParser(parser: GLTFParser): void {
  if (textureBudgetMaxDimension() <= 0) return;
  if (typeof createImageBitmap === "undefined") return;
  const current = parser.textureLoader;
  if (!(current instanceof ImageBitmapLoader)) return;
  if (current instanceof BudgetImageBitmapLoader) return;
  parser.textureLoader = new BudgetImageBitmapLoader(current.manager);
}

/** True for a texture whose image this module may safely rebuild. */
function isCappableTexture(texture: Texture): boolean {
  const flags = texture as Texture & {
    isCompressedTexture?: boolean;
    isDataTexture?: boolean;
    isVideoTexture?: boolean;
    isCubeTexture?: boolean;
  };
  if (
    flags.isCompressedTexture ||
    flags.isDataTexture ||
    flags.isVideoTexture ||
    flags.isCubeTexture
  ) {
    return false;
  }
  return isCappableImage(texture.image);
}

/**
 * Cap ONE texture's image in place. Textures cloned from the same glTF source
 * share their `Source`, so swapping `texture.image` (a `source.data` accessor)
 * reaches every clone at once. Returns whether anything changed.
 */
export async function capTextureImageInPlace(
  texture: Texture,
  maxDimension: number,
): Promise<boolean> {
  if (maxDimension <= 0 || !isCappableTexture(texture)) return false;
  const image = texture.image as CappableImage;
  const capped = await capDecodedImageAsync(image, maxDimension);
  if (capped === image) return false;
  texture.image = capped;
  texture.needsUpdate = true;
  return true;
}

const K_TEXTURE_SLOTS = [
  "map",
  "normalMap",
  "aoMap",
  "roughnessMap",
  "metalnessMap",
  "emissiveMap",
  "bumpMap",
  "displacementMap",
  "alphaMap",
  "lightMap",
  "specularMap",
  "clearcoatMap",
  "clearcoatNormalMap",
  "clearcoatRoughnessMap",
  "sheenColorMap",
  "sheenRoughnessMap",
  "transmissionMap",
  "thicknessMap",
  "specularIntensityMap",
  "specularColorMap",
  "iridescenceMap",
  "iridescenceThicknessMap",
  "anisotropyMap",
] as const;

/**
 * Post-parse belt: cap every material texture under `root`. De-duplicated by
 * `texture.source` so an image shared by several textures/materials is
 * rebuilt once. Runs serially through the decode queue.
 *
 * @returns how many texture images were rebuilt.
 */
export async function enforceTextureBudgetOnObject(
  root: Object3D,
  maxDimension: number,
): Promise<number> {
  if (maxDimension <= 0) return 0;
  const textures: Texture[] = [];
  const seenSources = new Set<unknown>();
  root.traverse((node) => {
    const mesh = node as Object3D & { material?: Material | Material[] };
    if (!mesh.material) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      const record = material as unknown as Record<string, unknown>;
      for (const slot of K_TEXTURE_SLOTS) {
        const value = record[slot] as (Texture & { source?: unknown }) | null | undefined;
        if (!value || !(value as Texture).isTexture) continue;
        const sourceKey = value.source ?? value;
        if (seenSources.has(sourceKey)) continue;
        seenSources.add(sourceKey);
        textures.push(value);
      }
    }
  });

  let rebuilt = 0;
  for (const texture of textures) {
    if (await capTextureImageInPlace(texture, maxDimension)) rebuilt++;
  }
  return rebuilt;
}

/**
 * Cap a parsed glTF's textures (belt over the decode-time plugin) and release
 * the parse-time source state the loader retains on the result: `gltf.parser`
 * keeps the WHOLE GLB binary chunk, a per-image bufferView slice, and a
 * per-source `Texture` whose image is the full-size decode — for a 40 MB GLB
 * with 8K maps that is ~100 MB of dead weight per asset, alive for the
 * project's lifetime because `GltfAssetCache` holds the `GLTF`. Nothing in
 * `src/` reads `gltf.parser` (verified 2026-08-27 — consumers use only
 * `gltf.scene` / `gltf.animations`), so it is dropped outright once the belt
 * has run. The parser strip runs whether or not capping is on: it is a
 * retention fix, not a quality trade.
 */
export async function enforceTextureBudgetOnGltf(gltf: GLTF): Promise<void> {
  const max = textureBudgetMaxDimension();
  if (max > 0) {
    try {
      await enforceTextureBudgetOnObject(gltf.scene, max);
    } catch (err) {
      console.warn("[textureBudget] post-parse texture pass failed:", err);
    }
  }
  (gltf as { parser?: unknown }).parser = undefined;
}
